import React, { FormEvent, ReactChildren, ReactElement, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    BoxStyleProps,
    Checkbox,
    Input,
    Box,
    Label,
    TextArea,
    Button,
    Text,
    RadioGroup,
    Radio
} from "@twilio-paste/core";

import { sessionDataHandler } from "../sessionDataHandler";
import { addNotification, changeEngagementPhase, updatePreEngagementData } from "../store/actions/genericActions";
import { initSession } from "../store/actions/initActions";
import { AppState, EngagementPhase, PreEngagementData } from "../store/definitions";
import { Header } from "./Header";
import { notifications } from "../notifications";
import { NotificationBar } from "./NotificationBar";
import { introStyles, fieldStyles, formStyles } from "./styles/PreEngagementFormPhase.styles";
import { PreEngagementField } from "../definitions";
import { ACTION_UPDATE_SESSION_ERROR_MESSAGE } from "../store/actions/actionTypes";

const checkboxFieldStyles: BoxStyleProps = {
    display: "flex",
    justifyContent: "space-between",
    width: "70%"
};

const FieldLabel = ({ children, field }: { children?: ReactChildren; field: PreEngagementField }) => {
    if (!field.label) {
        return <></>;
    }
    return (
        <>
            <Label htmlFor={field.attributes.name}>
                {field.label}
                {children}
                {field.attributes.required ? " *" : ""}
            </Label>
        </>
    );
};

export const PreEngagementFormPhase = () => {
    const formState = useSelector((state: AppState) => state.session.preEngagementData) || {};
    const sessionPreEngagementData: PreEngagementData =
        useSelector((state: AppState) => state.session.sessionPreEngagementData) || {};
    const { config, session } = useSelector((state: AppState) => state) || {};
    const preEngagementContext: PreEngagementData = {
        ...config?.context,
        ...sessionPreEngagementData
    };
    // const { sessionData } = useSelector((state: AppState) => state.config) || {};
    const dispatch = useDispatch();
    const fetchSessionForSubmit = async (formData: PreEngagementData) => {
        if (!config?.resumeSessionConfig?.showPreEngagementForm) {
            return {
                data: await sessionDataHandler.fetchAndStoreNewSession({ formData })
            };
        }

        const webchatSessionResponse = await sessionDataHandler.fetchWebchatSession({
            resumeSessionConfig: config.resumeSessionConfig,
            contextData: config.context
        });

        if (webchatSessionResponse?.sessionData) {
            return {
                data: webchatSessionResponse.sessionData
            };
        }

        if (webchatSessionResponse?.sessionMode === "new") {
            return {
                data: await sessionDataHandler.fetchAndStoreNewSession({
                    formData: {
                        ...formData,
                        ...webchatSessionResponse.sessionPreEngagementData
                    }
                })
            };
        }

        throw Error("No results from server");
    };
    const handleSubmit = async (e?: FormEvent) => {
        e?.preventDefault();
        dispatch(changeEngagementPhase({ phase: EngagementPhase.Loading }));
        try {
            const formData: PreEngagementData = {
                ...config?.context,
                ...formState,
                ...sessionPreEngagementData
            };
            if (formState?.friendlyName) {
                formData.customerName = formState?.friendlyName;
            }
            const { data } = await fetchSessionForSubmit(formData);
            dispatch(initSession(data, { mode: "engagementStart" }));
        } catch (err) {
            dispatch({
                type: ACTION_UPDATE_SESSION_ERROR_MESSAGE,
                payload: { error: (err as Error).message }
            });
            dispatch(addNotification(notifications.failedToInitSessionNotification((err as Error).message)));
            dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
        }
    };
    const getFieldComponent = (field: PreEngagementField): ReactElement => {
        if (field.attributes.name === "dummy_field") {
            return (
                <Box {...fieldStyles} element="PRE_ENGAGEMENT__ENTRY-CONTAINER">
                    <FieldLabel field={field} />
                </Box>
            );
        }
        switch (field.type) {
            case "TextareaItem":
                return (
                    <Box {...fieldStyles} element="PRE_ENGAGEMENT__ENTRY-CONTAINER">
                        <FieldLabel field={field} />
                        <TextArea
                            {...field.attributes}
                            value={formState[field.attributes.name]}
                            onChange={(e) =>
                                dispatch(updatePreEngagementData({ [field.attributes.name]: e.target.value }))
                            }
                        />
                    </Box>
                );
            case "InputItem":
            default:
                switch (field.attributes.type) {
                    case "checkbox":
                        return (
                            <Box
                                {...fieldStyles}
                                {...checkboxFieldStyles}
                                element="PRE_ENGAGEMENT__SELECTION-CONTAINER"
                            >
                                <FieldLabel field={field} />
                                <Checkbox
                                    id={field.attributes.name}
                                    name={field.attributes.name}
                                    checked={Boolean(formState[field.attributes.name])}
                                    onChange={(e) => {
                                        dispatch(
                                            updatePreEngagementData({ [field.attributes.name]: e.target.checked })
                                        );
                                    }}
                                >
                                    {""}
                                </Checkbox>
                            </Box>
                        );
                    case "radio":
                        return (
                            <Box
                                {...fieldStyles}
                                {...checkboxFieldStyles}
                                element="PRE_ENGAGEMENT__SELECTION-CONTAINER"
                            >
                                <FieldLabel field={field} />
                                <RadioGroup
                                    id={field.attributes.name}
                                    legend={field.attributes.legend || ""}
                                    helpText={field.attributes.helpText}
                                    name={field.attributes.name}
                                    value={formState[field.attributes.name]}
                                    onChange={(value) => {
                                        dispatch(updatePreEngagementData({ [field.attributes.name]: value }));
                                    }}
                                >
                                    {React.Children.toArray(
                                        field.attributes.options?.map((option, i) => {
                                            return (
                                                <Radio
                                                    id={`${field.attributes.name}_${i}`}
                                                    value={option.value}
                                                    name={field.attributes.name}
                                                >
                                                    {option.label}
                                                </Radio>
                                            );
                                        })
                                    )}
                                </RadioGroup>
                            </Box>
                        );

                    default:
                        return (
                            <Box {...fieldStyles} element="PRE_ENGAGEMENT__ENTRY-CONTAINER">
                                <FieldLabel field={field} />
                                {/*
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore */}
                                <Input
                                    {...field.attributes}
                                    value={formState[field.attributes.name]}
                                    onChange={(e) =>
                                        dispatch(updatePreEngagementData({ [field.attributes.name]: e.target.value }))
                                    }
                                />
                            </Box>
                        );
                }
        }
    };
    const getFieldComponents = () => {
        return React.Children.toArray(
            config?.preEngagementConfig?.fields
                .filter((field) => {
                    if (preEngagementContext?.[field.attributes.name]) {
                        return false;
                    }
                    return true;
                })
                .map((field) => {
                    return getFieldComponent(field);
                })
        );
    };
    useEffect(() => {
        if (config.preEngagementConfig?.skip && !config.resumeSessionConfig?.showPreEngagementForm && !session.error) {
            handleSubmit();
        }
    }, [config.preEngagementConfig?.skip, session.error, config.resumeSessionConfig?.showPreEngagementForm]);

    return (
        <>
            <Header />
            <NotificationBar />
            <Box
                element="MAIN__CONTAINER_INNER"
                as="form"
                className="twilio__pre-engagement-form"
                data-test="pre-engagement-chat-form"
                onSubmit={handleSubmit}
                {...formStyles}
            >
                {/* <Text {...titleStyles} as="h3">
                    Hi there!
                </Text> */}
                <Text {...introStyles} as="p" element="PRE_ENGAGEMENT__DESCRIPTION">
                    {config.preEngagementConfig?.description}
                </Text>
                {getFieldComponents()}
                <Button
                    variant="primary"
                    type="submit"
                    data-test="pre-engagement-start-chat-button"
                    className="twilio__start-chat-button"
                    element="SUBMIT__BUTTON"
                >
                    {config.preEngagementConfig?.submitLabel || ""}
                </Button>
            </Box>
        </>
    );
};
